import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useUser } from '@clerk/tanstack-react-start';
import { useMutation, useQuery } from 'convex/react';
import { Button, FragmentsLogo, Text } from '@fragments-sdk/ui';
import { api, type Id } from '@/convex';

export const Route = createFileRoute('/invites')({
  component: InvitesPage,
  validateSearch: (search: Record<string, unknown>) => ({
    invite: typeof search.invite === 'string' ? search.invite : undefined,
  }),
});

function InvitesPage() {
  const { invite } = Route.useSearch();
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const acceptInvite = useMutation(api.invites.accept);
  const [error, setError] = React.useState('');
  const [accepting, setAccepting] = React.useState(false);

  const email = user?.primaryEmailAddress?.emailAddress?.toLowerCase();
  const pendingInvites = useQuery(
    api.invites.listPendingForEmail,
    user && email ? { email } : 'skip',
  );
  const selectedInvite = invite
    ? pendingInvites?.find((item) => item._id === invite)
    : pendingInvites?.[0];

  React.useEffect(() => {
    if (!isLoaded || user) return;
    navigate({
      to: '/sign-in',
      search: invite ? { invite } : {},
      replace: true,
    });
  }, [invite, isLoaded, navigate, user]);

  async function handleAccept() {
    if (!user || !selectedInvite) return;

    setAccepting(true);
    setError('');
    try {
      await acceptInvite({
        inviteId: selectedInvite._id as Id<'invites'>,
        clerkUserId: user.id,
      });
      navigate({ to: '/', replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  }

  if (!isLoaded || !user || pendingInvites === undefined) {
    return (
      <Shell>
        <Text as="h1" size="xl" weight="bold">
          Loading invite
        </Text>
        <Text color="secondary">
          Checking the invite against your signed-in email.
        </Text>
      </Shell>
    );
  }

  if (!selectedInvite) {
    return (
      <Shell>
        <Text as="h1" size="xl" weight="bold">
          Invite unavailable
        </Text>
        <Text color="secondary">
          This invite may be expired, already accepted, or tied to another email
          address.
        </Text>
        <Button variant="outline" onClick={() => navigate({ to: '/' })}>
          Continue
        </Button>
      </Shell>
    );
  }

  return (
    <Shell>
      <Text as="h1" size="xl" weight="bold">
        Join {selectedInvite.orgName}
      </Text>
      <Text color="secondary">
        You were invited as a {selectedInvite.role}. Accepting adds this
        workspace to your account.
      </Text>
      {error && (
        <Text size="sm" color="primary">
          {error}
        </Text>
      )}
      <Button
        variant="primary"
        onClick={() => void handleAccept()}
        disabled={accepting}
      >
        {accepting ? 'Accepting...' : 'Accept invite'}
      </Button>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background: 'var(--fui-bg-primary)',
        color: 'var(--fui-fg-primary)',
      }}
    >
      <section
        style={{
          display: 'grid',
          gap: 16,
          maxWidth: 440,
          textAlign: 'center',
          justifyItems: 'center',
        }}
      >
        <FragmentsLogo size={40} />
        {children}
      </section>
    </main>
  );
}
